import { useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import { Line2 } from 'three/examples/jsm/lines/Line2.js'
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js'

let rafId

const Lightning = () => {
    const mountRef = useRef(null)
    const noise = () => (Math.random() - 0.5)
    const [isMobile, setIsMobile] = useState(
        window.matchMedia('(max-width: 768px)').matches
    )

    useEffect(() => {
        const media = window.matchMedia('(max-width: 768px)')
        const handler = (e) => setIsMobile(e.matches)

        media.addEventListener('change', handler)
        return () => media.removeEventListener('change', handler)
    }, [])


    useEffect(() => {
        const scene = new THREE.Scene()

        const isInteractiveTarget = (e) => {
            return (
                e.target.closest('input') ||
                e.target.closest('textarea') ||
                e.target.closest('button') ||
                e.target.closest('a') ||
                e.target.closest('nav')
            )
        }

        const camera = new THREE.PerspectiveCamera(
            60,
            window.innerWidth / window.innerHeight,
            1,
            1000
        )
        camera.position.z = 120

        if (!mountRef.current) return

        const renderer = new THREE.WebGLRenderer({ alpha: true })
        renderer.setSize(window.innerWidth, window.innerHeight)
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

        mountRef.current.appendChild(renderer.domElement)


        renderer.domElement.style.position = 'fixed'
        renderer.domElement.style.inset = '0'
        renderer.domElement.style.zIndex = '-1'
        renderer.domElement.style.pointerEvents = 'none'

        const group = new THREE.Group()
        scene.add(group)

        const bolts = []
        const attractor = new THREE.Vector3()
        let isActive = false
        let holdTime = 0
        let spawnTimer = 0
        const spawnInterval = 0.05 
        let lastTime = performance.now() / 1000

        const purple = new THREE.Color('#a600ff')
        const silver = new THREE.Color('#9ba1ab')

        
        const flattenPoints = (points) => {
            return points.reduce((acc, p) => {
                acc.push(p.x, p.y, p.z)
                return acc
            }, [])
        }

        const randomStartPoint = () => {
            const edge = Math.floor(Math.random() * 4)

            switch (edge) {
                case 0: 
                    return { x: (Math.random() - 0.5) * 160, y: 100 }
                case 1: 
                    return { x: (Math.random() - 0.5) * 160, y: -100 }
                case 2: 
                    return { x: -160, y: (Math.random() - 0.5) * 90 }
                case 3: 
                    return { x: 160, y: (Math.random() - 0.5) * 90 }
            }
        }


        const spawnBolt = () => {
            const points = []
            const velocity = []

            const start = randomStartPoint()
            let x = start.x
            let y = start.y
            const z = (Math.random() - 0.5) * 20

            
            for (let i = 0; i < 22; i++) {
                x += (Math.random() - 0.5) * 15
                y += (Math.random() - 0.5) * 15
                points.push(new THREE.Vector3(x, y, z))
                velocity.push(new THREE.Vector3())
            }

            const flatPositions = flattenPoints(points)
            const geometry = new LineGeometry()
            geometry.setPositions(flatPositions)

            
            const materials = [
                
                new LineMaterial({
                    color: purple,
                    linewidth: 2.6,
                    transparent: true,
                    opacity: 0.55,
                    blending: THREE.AdditiveBlending,
                    depthTest: false,
                }),

                
                new LineMaterial({
                    color: silver,
                    linewidth: 2,
                    transparent: true,
                    opacity: 0.95,
                    blending: THREE.NormalBlending, 
                    depthTest: false,
                }),
            ]

            materials.forEach(mat => {
                mat.resolution.set(window.innerWidth, window.innerHeight)
            })

            const lines = materials.map(mat => {
                const line = new Line2(geometry.clone(), mat)
                group.add(line)
                return line
            })

            bolts.push({
                points,
                velocity,
                lines,
                life: 1,
                locked: false,
            })


            
            if (Math.random() > 0.5) {
                spawnBranch(points[Math.floor(points.length * 0.7)])
            }
        }

        const spawnBranch = (startPoint) => {
            const points = [startPoint.clone()]
            const velocity = [new THREE.Vector3()]

            let x = startPoint.x
            let y = startPoint.y
            const z = startPoint.z + (Math.random() - 0.5) * 10

            for (let i = 0; i < 10; i++) {
                x += (Math.random() - 0.5) * 10
                y += (Math.random() - 0.5) * 10
                points.push(new THREE.Vector3(x, y, z))
                velocity.push(new THREE.Vector3())
            }

            const flatPositions = flattenPoints(points)
            const geometry = new LineGeometry()
            geometry.setPositions(flatPositions)

            const materials = [
                new LineMaterial({
                    color: purple,
                    linewidth: 0.2, 
                    transparent: true,
                    opacity: 0.25,
                    blending: THREE.AdditiveBlending,
                    depthTest: false,
                }),
                new LineMaterial({
                    color: silver,
                    linewidth: 1,
                    transparent: true,
                    opacity: 0.7,
                    blending: THREE.AdditiveBlending,
                    depthTest: false,
                }),
            ]

            materials.forEach(mat => {
                mat.resolution.set(window.innerWidth, window.innerHeight)
            })

            const lines = materials.map(mat => {
                const line = new Line2(geometry.clone(), mat)
                group.add(line)
                return line
            })

            bolts.push({
                points,
                velocity,
                lines,
                life: 1,
                locked: false,
            })
        }

        const animate = () => {
            const now = performance.now() / 1000
            const delta = now - lastTime
            lastTime = now
            if (isActive) {
                holdTime += delta
                spawnTimer += delta

                
                while (spawnTimer > spawnInterval) {
                    spawnBolt()
                    spawnTimer -= spawnInterval
                }
            } else {
                holdTime = 0
                spawnTimer = 0
            }


            for (let i = bolts.length - 1; i >= 0; i--) {
                const bolt = bolts[i]
                let closePoints = 0

                bolt.points.forEach((p, idx) => {
                    const dx = attractor.x - p.x
                    const dy = attractor.y - p.y
                    const dist = Math.sqrt(dx * dx + dy * dy) + 0.001

                    
                    const t = idx / bolt.points.length          
                    const tipBias = t * t                       
                    const charge = Math.min(holdTime * 2.2, 4)
                    const force = (220 * tipBias * charge) / Math.max(dist, 6)

                    if (bolt.locked) {
                        bolt.velocity[idx].multiplyScalar(0.85)
                    }


                    if (dist < 18 && !bolt.locked) {
                        bolt.locked = true
                    }


                    if (!bolt.locked) {
                        bolt.velocity[idx].x += (dx / dist) * force
                        bolt.velocity[idx].y += (dy / dist) * force
                    }


                    

  
                    
                    const chaos = bolt.locked ? 0.25 : 0.7
                    p.x += noise() * chaos
                    p.y += noise() * chaos
                    const taper = 1 - idx / bolt.points.length
                    p.multiplyScalar(0.995 + taper * 0.005)


                    p.add(bolt.velocity[idx].multiplyScalar(0.92)) 

                    
                    if (dist < 2) closePoints++
                })

                const flatPositions = flattenPoints(bolt.points)
                bolt.lines.forEach(line => {
                    line.geometry.setPositions(flatPositions)
                  
                    line.material.opacity = bolt.life * (0.6 + Math.random() * 0.4)

                })

                
                if (bolt.locked) {
                    bolt.life -= 0.04
                } else {
                    bolt.life -= 0.015
                }


                
                if (closePoints > 10) bolt.life -= 0.05

                if (bolt.life <= 0) {
                    bolt.lines.forEach(l => {
                        l.geometry.dispose()
                        l.material.dispose()
                        group.remove(l)
                    })
                    bolts.splice(i, 1)
                }
            }

            renderer.render(scene, camera)
            rafId = requestAnimationFrame(animate)
        }

        animate()

        
        const toWorld = (e) => {
            const clientX = e.clientX || (e.touches && e.touches[0].clientX)
            const clientY = e.clientY || (e.touches && e.touches[0].clientY)
            attractor.x = (clientX / window.innerWidth) * 320 - 160
            attractor.y = -(clientY / window.innerHeight) * 180 + 90
            attractor.z = 0
        }

        const down = (e) => {
            if (isInteractiveTarget(e)) return
            e.preventDefault()
            isActive = true
            toWorld(e)

            for (let i = 0; i < 8; i++) spawnBolt()
        }

        const move = (e) => {
            if (!isActive || isInteractiveTarget(e)) return
            e.preventDefault()
            toWorld(e)
        }

        const up = (e) => {
            if (isInteractiveTarget(e)) return
            e.preventDefault()
            isActive = false
        }


        if (!isMobile) {
            window.addEventListener('pointerdown', down)
            window.addEventListener('pointermove', move)
            window.addEventListener('pointerup', up)

            window.addEventListener('touchstart', down, {passive: false})
            window.addEventListener('touchmove', move, {passive: false})
            window.addEventListener('touchend', up, {passive: false})
        }
        const resize = () => {
            if (!mountRef.current) return

            const width = mountRef.current.clientWidth
            const height = mountRef.current.clientHeight

            renderer.setSize(width, height)
            camera.aspect = width / height
            camera.updateProjectionMatrix()

            renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5))

            bolts.forEach(bolt => {
                bolt.lines.forEach(line => {
                    line.material.resolution.set(width, height)
                })
            })
        }

        window.addEventListener('resize', resize)

        return () => {
            if (rafId) cancelAnimationFrame(rafId)

            window.removeEventListener('resize', resize)
            if (!isMobile) {
                window.removeEventListener('pointerdown', down)
                window.removeEventListener('pointermove', move)
                window.removeEventListener('pointerup', up)

                window.removeEventListener('touchstart', down)
                window.removeEventListener('touchmove', move)
                window.removeEventListener('touchend', up)
            }
            bolts.forEach(bolt => {
                bolt.lines.forEach(l => {
                    l.geometry.dispose()
                    l.material.dispose()
                    group.remove(l)
                })
            })

            renderer.dispose()

            if (
                mountRef.current &&
                renderer.domElement &&
                renderer.domElement.parentNode === mountRef.current
            ) {
                mountRef.current.removeChild(renderer.domElement)
            }
        }
    }, [])

    return (
        <div
            ref={mountRef}
            style={{
                position: 'fixed',
                inset: 0,
                width: '100%',
                height: '100%',
                zIndex: 0,
                pointerEvents: 'none',
            }}
        />
    )

}

export default Lightning